
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { isNoteMessage } from '@/utils/message-helpers';

interface UseUnreadMessagesOptions {
  userId?: string;
  userType: 'client' | 'practitioner';
  globalRefreshTrigger?: number;
}

interface UnreadMessageCounts {
  totalUnread: number;
  unreadByBooking: Record<string, number>;
  chatUnreadByBooking: Record<string, number>;
  noteUnreadByBooking: Record<string, number>;
  chatTotalUnread: number;
  noteTotalUnread: number;
}

export function useUnreadMessages({ userId: providedUserId, userType, globalRefreshTrigger }: UseUnreadMessagesOptions) {
  const { user } = useAuth();
  const userId = providedUserId || user?.id;

  const [unreadCounts, setUnreadCounts] = useState<UnreadMessageCounts>({
    totalUnread: 0,
    unreadByBooking: {},
    chatUnreadByBooking: {},
    noteUnreadByBooking: {},
    chatTotalUnread: 0,
    noteTotalUnread: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const isRefreshing = useRef(false);

  const fetchUnreadCounts = async () => {
    if (!userId || isRefreshing.current) return;

    try {
      isRefreshing.current = true;

      const { data, error } = await supabase
        .from('messages')
        .select(`
          id,
          conversation_id,
          is_read,
          sender_type,
          message_content,
          conversations!inner(
            booking_id,
            bookings!inner(
              ${userType === 'client' ? 'client_id' : 'practitioner_id'}
            )
          )
        `)
        .eq(`conversations.bookings.${userType === 'client' ? 'client_id' : 'practitioner_id'}`, userId)
        .neq('sender_type', userType);

      if (error) {
        console.error('Error fetching unread messages:', error);
        return;
      }

      // Filter to only unread messages (is_read === false or is_read === null)
      const unreadData = data?.filter(msg => msg.is_read === false || msg.is_read === null) || [];

      // Calculate split unread counts
      const unreadByBooking: Record<string, number> = {};
      const chatUnreadByBooking: Record<string, number> = {};
      const noteUnreadByBooking: Record<string, number> = {};
      let totalUnread = 0;
      let chatTotalUnread = 0;
      let noteTotalUnread = 0;

      unreadData.forEach(message => {
        const bookingId = message.conversations.booking_id;
        const isNote = isNoteMessage(message.message_content);

        unreadByBooking[bookingId] = (unreadByBooking[bookingId] || 0) + 1;
        totalUnread++;

        if (isNote) {
          noteUnreadByBooking[bookingId] = (noteUnreadByBooking[bookingId] || 0) + 1;
          noteTotalUnread++;
        } else {
          chatUnreadByBooking[bookingId] = (chatUnreadByBooking[bookingId] || 0) + 1;
          chatTotalUnread++;
        }
      });

      setUnreadCounts({
        totalUnread,
        unreadByBooking,
        chatUnreadByBooking,
        noteUnreadByBooking,
        chatTotalUnread,
        noteTotalUnread,
      });
    } catch (error) {
      console.error('Error in fetchUnreadCounts:', error);
    } finally {
      setIsLoading(false);
      isRefreshing.current = false;
    }
  };

  const markMessagesAsRead = async (bookingId: string, messageType: 'chat' | 'note' | 'all' = 'all') => {
    if (!userId) return;

    try {
      // First get the conversation_id for this booking
      const { data: conversationData, error: conversationError } = await supabase
        .from('conversations')
        .select('id')
        .eq('booking_id', bookingId)
        .single();

      if (conversationError || !conversationData) {
        console.error('Error finding conversation for booking:', conversationError);
        return;
      }

      if (messageType === 'all') {
        // Original behavior: mark all unread messages
        const { error } = await supabase
          .from('messages')
          .update({ is_read: true })
          .eq('conversation_id', conversationData.id)
          .eq('is_read', false)
          .neq('sender_type', userType);

        if (error) {
          console.error('Error marking messages as read:', error);
          return;
        }
      } else {
        // Type-aware: fetch IDs first, filter by type, then update
        const { data: unreadMessages, error: fetchError } = await supabase
          .from('messages')
          .select('id, message_content')
          .eq('conversation_id', conversationData.id)
          .eq('is_read', false)
          .neq('sender_type', userType);

        if (fetchError || !unreadMessages) {
          console.error('Error fetching unread messages:', fetchError);
          return;
        }

        const idsToMark = unreadMessages
          .filter(msg => {
            const msgIsNote = isNoteMessage(msg.message_content);
            return messageType === 'note' ? msgIsNote : !msgIsNote;
          })
          .map(msg => msg.id);

        if (idsToMark.length === 0) return;

        const { error } = await supabase
          .from('messages')
          .update({ is_read: true })
          .in('id', idsToMark);

        if (error) {
          console.error('Error marking messages as read:', error);
          return;
        }
      }

      // Update local state immediately
      setUnreadCounts(prev => {
        const newState = { ...prev };

        if (messageType === 'all' || messageType === 'chat') {
          const chatCount = prev.chatUnreadByBooking[bookingId] || 0;
          newState.chatTotalUnread = prev.chatTotalUnread - chatCount;
          newState.chatUnreadByBooking = { ...prev.chatUnreadByBooking };
          delete newState.chatUnreadByBooking[bookingId];
        }
        if (messageType === 'all' || messageType === 'note') {
          const noteCount = prev.noteUnreadByBooking[bookingId] || 0;
          newState.noteTotalUnread = prev.noteTotalUnread - noteCount;
          newState.noteUnreadByBooking = { ...prev.noteUnreadByBooking };
          delete newState.noteUnreadByBooking[bookingId];
        }

        // Recalculate totals
        newState.unreadByBooking = { ...prev.unreadByBooking };
        if (messageType === 'all') {
          delete newState.unreadByBooking[bookingId];
          newState.totalUnread = prev.totalUnread - (prev.unreadByBooking[bookingId] || 0);
        } else {
          const removed = messageType === 'chat'
            ? (prev.chatUnreadByBooking[bookingId] || 0)
            : (prev.noteUnreadByBooking[bookingId] || 0);
          newState.unreadByBooking[bookingId] = (prev.unreadByBooking[bookingId] || 0) - removed;
          if (newState.unreadByBooking[bookingId] <= 0) delete newState.unreadByBooking[bookingId];
          newState.totalUnread = prev.totalUnread - removed;
        }

        return newState;
      });

      console.log('✅ Messages marked as read for booking:', bookingId, 'type:', messageType);
    } catch (error) {
      console.error('Error in markMessagesAsRead:', error);
    }
  };

  // Initial fetch and refresh on globalRefreshTrigger changes
  useEffect(() => {
    if (userId) {
      fetchUnreadCounts();
    }
  }, [userId, userType, globalRefreshTrigger]);

  return {
    unreadCounts,
    isLoading,
    markMessagesAsRead,
    refreshUnreadCounts: fetchUnreadCounts
  };
}
